import { createContext, useContext, useState, useEffect } from 'react';
import { logoutAction } from '../utils/action';
import api from '../utils/api';

const AuthContext = createContext();

// Dipakai untuk menyampaikan pesan penolakan akses lintas-navigasi
// (preload -> halaman Login) karena penolakan bisa terjadi SEBELUM
// halaman Login sempat dirender (mis. buka app dengan sesi lama yang
// akses clp.*-nya sudah dicabut lewat SA).
export const CLP_ACCESS_DENIED_STORAGE_KEY = 'clp_access_denied_message';
const CLP_ACCESS_DENIED_MESSAGE = 'Akun Anda belum diberikan akses ke Campus Leaders Program. Hubungi Management untuk mengatur akses Anda.';

const hasClpAccess = (userData) => (userData?.activeSession?.permissions || []).some((code) => code.startsWith('clp.'));

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(() => {
    const storedUser = localStorage.getItem('authUser');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [loading, setLoading] = useState(!authUser);

  const preloadUser = async () => {
    try {
      const sessionRes = await api.getSessionContext();
      let userRes;

      if (sessionRes?.activeSession?.roleCode === 'ADMIN') {
        userRes = await api.getOwnBCFProfile();
      } else {
        userRes = await api.getOwnProfile();
      }

      const userData = {
        ...userRes.data,
        ...sessionRes,
      };

      // Session lama masih ada, tapi akses clp.*-nya sudah dicabut lewat SA
      // sejak terakhir login -- jangan biarkan tetap masuk ke dashboard admin.
      if (sessionRes?.activeSession?.roleCode === 'ADMIN' && !hasClpAccess(userData)) {
        api.deleteToken();
        localStorage.setItem(CLP_ACCESS_DENIED_STORAGE_KEY, CLP_ACCESS_DENIED_MESSAGE);
        setAuthUser(null);
        localStorage.removeItem('authUser');
        return;
      }

      setAuthUser(userData);
      localStorage.setItem('authUser', JSON.stringify(userData));
    } catch {
      setAuthUser(null);
      localStorage.removeItem('authUser');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Selalu refresh session saat app dimuat -- bukan hanya saat localStorage
    // kosong. Sebelumnya, employee yang sudah login sebelum permission-nya
    // diubah (lewat /roles atau /employee/:id/access di sa-web-app) tidak
    // akan pernah melihat efeknya sampai logout+login manual, karena
    // `authUser` di localStorage dianggap valid selamanya. Ini berjalan diam-
    // diam di background (tidak mengubah `loading` kalau authUser sudah ada
    // dari cache), jadi tidak ada flash/spinner tambahan untuk user yang
    // sudah login.
    preloadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Synchronize authUser to localStorage whenever it changes
  useEffect(() => {
    if (authUser) {
      localStorage.setItem('authUser', JSON.stringify(authUser));
    } else {
      localStorage.removeItem('authUser');
    }
  }, [authUser]);

  const refreshUser = async () => {
    try {
      const sessionRes = await api.getSessionContext();
      let userRes;

      if (sessionRes?.activeSession?.roleCode === 'ADMIN') {
        userRes = await api.getOwnBCFProfile();
      } else {
        userRes = await api.getOwnProfile();
      }

      const userData = {
        ...userRes.data,
        ...sessionRes,
      };
      setAuthUser(userData);
      localStorage.setItem('authUser', JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  };

  const signOut = () => {
    logoutAction();
    setAuthUser(null);
    localStorage.removeItem('authUser');
  };

  return (
    <AuthContext.Provider
      value={{ authUser, setAuthUser, signOut, loading, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
